import { DEFAULT_FEATURE_STATE, FeatureSwitchState, isFeatureState } from './featureState';
import { isRolloutState } from './rolloutState';

/**
 * The `config.key` whose `settings` jsonb holds the switch table:
 *
 * ```json
 * { "cloudflare-beta": "internal", "my-new-feature": { "state": "rollout", "percent": 15 } }
 * ```
 */
export const FEATURE_SWITCHES_CONFIG_KEY = 'feature_switches';

export type FeatureSwitches = Readonly<Record<string, FeatureSwitchState>>;

/**
 * Reads the switch table out of whatever the `config` row actually held.
 *
 * Every entry that is not a known state is dropped rather than rejected, which
 * holds a bad edit to the feature it was made on: that one falls back to
 * {@link DEFAULT_FEATURE_STATE} and closes, while the features around it keep
 * their states. Rejecting the whole row would instead close all of them.
 */
export function parseFeatureSwitches(value: unknown): FeatureSwitches {
  const parsed: Record<string, FeatureSwitchState> = Object.create(null);

  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return parsed;
  }

  for (const [flag, state] of Object.entries(value)) {
    if (isFeatureState(state) || isRolloutState(state)) {
      parsed[flag] = state;
    }
  }

  return parsed;
}

export function featureStateFor(switches: FeatureSwitches | null | undefined, flag: string): FeatureSwitchState {
  return switches?.[flag] ?? DEFAULT_FEATURE_STATE;
}
